import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the delivery promise in step with the selected variant.
 *
 * The promise is variant-dependent — it reads the variant's `custom.available`
 * metafield and its inventory quantity — but this block lives outside
 * `<variant-picker>`, whose default morph target is only itself. Without this
 * the label would keep whatever value the page first rendered with.
 *
 * Mirrors product-inventory.js: listen on the closest section for productSelect,
 * then pull the freshly rendered node out of the response document and morph.
 */
class SolsticeDeliveryPromise extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event by re-rendering the promise.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Quick-add and product cards dispatch the same event; they are not this product.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        // Combined listings can swap the product entirely; otherwise ignore an
        // event that belongs to a different product on the same page.
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector('solstice-delivery-promise');
        if (!next) return;

        morph(this, next, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[solstice-delivery-promise] Event promise rejected:', error);
        }
      });
  };
}

if (!customElements.get('solstice-delivery-promise')) {
  customElements.define('solstice-delivery-promise', SolsticeDeliveryPromise);
}
